// BL-177 Step 2d (section 4): Admin Center > Notification Defaults.
// Pure helpers (grouping, filter tabs, search, help text) plus the three Supabase calls the page makes for the
// company-wide switch. Channel overrides keep using bl177_set_org_notification_channel (migration 203).
import { notificationPreferenceChannels } from './notificationPreferences';
import { NOTIFICATION_HELP_CONTENT } from '../components/shared/notificationHelpContent';

// Column order on the board: Email, SMS, Push, In-app, Voice.
export const DEFAULTS_CHANNEL_COLUMNS = ['email', 'sms', 'push', 'in_app', 'voice'];

export const DEFAULTS_CHANNEL_LABELS = {
    email: 'Email',
    sms: 'SMS',
    push: 'Push',
    in_app: 'In-app',
    voice: 'Voice',
};

export const DEFAULTS_GROUPS = [
    { key: 'clients', label: 'Clients' },
    { key: 'staff', label: 'Staff' },
    { key: 'drivers', label: 'Drivers & affiliates' },
];

export const DEFAULTS_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'clients', label: 'Clients' },
    { key: 'drivers', label: 'Drivers' },
    { key: 'staff', label: 'Staff' },
    { key: 'affiliates', label: 'Affiliates' },
];

// Help lines written for this page, used only when a notification has no Self-Help article yet.
// (Listed in the PR as "Self-Help text written".)
export const FALLBACK_HELP = {
    clients: {
        what: 'A message to your client about their trip or account.',
        who: 'The client on the trip or account.',
    },
    staff: {
        what: 'An alert to your team about something that needs a look.',
        who: 'Your company\'s staff who handle this kind of work.',
    },
    drivers: {
        what: 'A message to a driver or affiliate about their work with you.',
        who: 'The driver or affiliate the event is about.',
    },
    when: 'Right after the event it is named for happens in IntelAgent.',
};

export function notificationGroup(eventType = '') {
    if (eventType.startsWith('client.')) return 'clients';
    if (eventType.startsWith('staff.')) return 'staff';
    return 'drivers';
}

function helpEntry(workflow) {
    return NOTIFICATION_HELP_CONTENT[workflow?.workflowKey] || NOTIFICATION_HELP_CONTENT[workflow?.eventType] || null;
}

// What / Who / When for the "?" panel: the Self-Help article where it exists, otherwise the short lines above.
export function notificationHelp(workflow) {
    const entry = helpEntry(workflow);
    if (entry) return { what: entry.definition, who: entry.who, when: entry.when, fromSelfHelp: true };
    const group = FALLBACK_HELP[notificationGroup(workflow?.eventType)];
    return { what: group.what, who: group.who, when: FALLBACK_HELP.when, fromSelfHelp: false };
}

// Affiliate-only: about affiliate drivers alone (payouts, the no-login status link). Shared trip messages that go
// to "the driver or affiliate" show under both the Drivers and the Affiliates tab.
export function isAffiliateOnly(workflow) {
    if (notificationGroup(workflow?.eventType) !== 'drivers') return false;
    if (/affiliate/i.test(workflow?.eventType || '')) return true;
    const entry = helpEntry(workflow);
    return /affiliate driver/i.test(`${entry?.definition || ''} ${entry?.who || ''}`);
}

export function isAffiliateNotification(workflow) {
    if (notificationGroup(workflow?.eventType) !== 'drivers') return false;
    if (isAffiliateOnly(workflow)) return true;
    return /\baffiliate\b/i.test(helpEntry(workflow)?.who || '');
}

export function matchesFilter(workflow, filter = 'all') {
    const group = notificationGroup(workflow?.eventType);
    switch (filter) {
        case 'clients': return group === 'clients';
        case 'staff': return group === 'staff';
        case 'drivers': return group === 'drivers' && !isAffiliateOnly(workflow);
        case 'affiliates': return isAffiliateNotification(workflow);
        default: return true;
    }
}

export function matchesSearch(workflow, query = '') {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    const entry = helpEntry(workflow);
    return [workflow?.displayName, workflow?.eventType, entry?.definition]
        .filter(Boolean)
        .some((text) => text.toLowerCase().includes(needle));
}

// Which of the five columns this notification can use; the rest show "—".
export function availableChannels(workflow) {
    const allowed = notificationPreferenceChannels(workflow);
    return DEFAULTS_CHANNEL_COLUMNS.filter((channel) => allowed.includes(channel));
}

// eventType -> enabled. No row = ON.
export function buildSwitchMap(rows = []) {
    return (rows || []).reduce((map, row) => {
        if (row?.event_type && typeof row.enabled === 'boolean') map[row.event_type] = row.enabled;
        return map;
    }, {});
}

export function companyLabel(companyName) {
    const name = (companyName || '').trim();
    return name || 'your company';
}

export function possessive(name) {
    return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

function withOrg(args, orgId) {
    return orgId ? { ...args, p_org_id: orgId } : args;
}

export async function loadCompanySwitches(client, orgId) {
    let query = client.from('org_notification_switches').select('event_type, enabled');
    if (orgId) query = query.eq('org_id', orgId);
    const { data, error } = await query;
    if (error) throw error;
    return buildSwitchMap(data);
}

// Returns the value the server confirmed (falls back to the value asked for when the RPC answers without one).
export async function setCompanySwitch(client, { eventType, enabled, orgId }) {
    const { data, error } = await client.rpc('notification_set_company_switch', withOrg({ p_event_type: eventType, p_enabled: enabled }, orgId));
    if (error) throw error;
    // The RPC answers with bl177_org_notification_state(...), whose company_enabled is the stored switch.
    if (data && typeof data.company_enabled === 'boolean') return data.company_enabled;
    if (data && typeof data.enabled === 'boolean') return data.enabled;
    return enabled;
}

export async function resetCompanyDefaults(client, { orgId } = {}) {
    const { data, error } = await client.rpc('notification_reset_company_defaults', withOrg({}, orgId));
    if (error) throw error;
    return data;
}

// Platform admins and this company's admins only (the same two checks the /admin route guard uses).
export async function canManageNotificationDefaults(client) {
    const [orgAdmin, platformAdmin] = await Promise.all([
        client.rpc('is_org_admin'),
        client.rpc('is_platform_super_admin'),
    ]);
    if (orgAdmin?.error && platformAdmin?.error) throw orgAdmin.error;
    return orgAdmin?.data === true || platformAdmin?.data === true;
}
